import { useNavigate, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/data/exercises";
import { getGardien } from "@/data/gardiens";
import { GardienBadge, GardienAvatar } from "@/components/GardienBadge";
import api from "@/lib/api";
import { TrialBanner } from "@/components/TrialBanner";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowRight, CalendarDays, CreditCard, AlertTriangle, Mail, BookOpen, Sparkles } from "lucide-react";

const CARDS = [
  {
    to: "/tableau-de-bord", testId: "space-soin", gardien: "theodore",
    title: "Soignant & Patient", sub: "Animer des ateliers et suivre l'évolution cognitive",
    points: ["Profils des patients", "Ateliers & exercices", "Suivi des progrès"],
    cls: "bg-primary text-primary-foreground", dot: "bg-primary-foreground",
  },
  {
    to: "/famille", testId: "space-famille", gardien: "rose-auguste",
    title: "Aidant & Famille", sub: "Partager, se souvenir et prendre plaisir à jouer ensemble",
    points: ["Le plaisir de se retrouver", "Jeux & souvenirs", "Témoignages des familles"],
    cls: "bg-warm/10 border border-warm/30 text-foreground", dot: "bg-warm",
  },
  {
    to: "/rejoindre", testId: "space-residence", gardien: "eloise",
    title: "Famille d'un résident", sub: "Nouvelles de mon proche en établissement, avec le code de l'équipe soignante",
    points: ["Nouvelles et progrès", "Voix et messages à l'équipe", "Accès gratuit pour les familles"],
    cls: "bg-white border border-sky-200 text-foreground", dot: "bg-sky-500",
  },
];

const LINKS = [
  { to: "/gardiens", testId: "hub-gardiens-button", icon: Sparkles, label: "Rencontrer les Gardiens" },
  { to: "/ephemeride", testId: "hub-ephemeride-button", icon: CalendarDays, label: "L'éphéméride du jour" },
  { to: "/contes", testId: "hub-stories-button", icon: BookOpen, label: "Contes & Poésie" },
  { to: "/abonnement", testId: "hub-subscribe-button", icon: CreditCard, label: "Abonnement / Tarifs" },
  { to: "/contact", testId: "hub-contact-button", icon: Mail, label: "Nous contacter" },
];

function SpaceCard({ c, onClick, locked }) {
  const g = getGardien(c.gardien);
  return (
    <button onClick={onClick} data-testid={c.testId} data-locked={locked ? "true" : "false"}
      className={`relative group text-left rounded-[2rem] p-8 sm:p-10 flex flex-col min-h-[20rem] ${locked ? "opacity-80" : ""} hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] active:scale-[0.98] transition-[transform,box-shadow] duration-500 ${c.cls}`}>
      {locked && <span className="absolute top-5 right-5 rounded-full bg-white text-amber-800 text-xs font-bold px-3 py-1.5 shadow-sm" data-testid={`${c.testId}-locked`}>Abonnement requis</span>}
      <div className="flex items-center gap-4 mb-6">
        <GardienAvatar id={g.id} className="w-20 h-20 border-4" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-75">Avec {g.name}</p>
          <p className="font-serif-display italic opacity-90 leading-snug">« {g.quote} »</p>
        </div>
      </div>
      <h2 className="font-serif-display text-3xl sm:text-4xl font-light mb-2">{c.title}</h2>
      <p className="text-lg opacity-80 mb-6">{c.sub}</p>
      <ul className="space-y-1.5 mb-6">
        {c.points.map((p) => (
          <li key={p} className="flex items-center gap-2 opacity-90"><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> {p}</li>
        ))}
      </ul>
      <span className="mt-auto inline-flex items-center gap-2 font-bold text-lg">
        Entrer <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
      </span>
    </button>
  );
}

export default function SpaceHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expiry, setExpiry] = useState(null);
  const [homeOnly, setHomeOnly] = useState(false);
  const familyHome = user?.space === "residence" ? "/nouvelles" : user?.role === "family" ? "/famille" : null;
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/paypal/subscription").then(({ data }) => {
      setSub(data);
      setHomeOnly((!!data.free && !data.paid) || !!data.trial_expired);
      if (!data.active_until) return;
      const until = new Date(data.active_until);
      const days = Math.ceil((until - new Date()) / 86400000);
      if (days <= 7) setExpiry({ until, days, active: data.active });
    }).catch(() => {});
  }, []);

  if (familyHome) return <Navigate to={familyHome} replace />;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 lg:px-10 py-5 border-b border-border/50">
        <img src={LOGO} alt="Passerelle" className="h-11 w-auto" />
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/connexion"); }} data-testid="hub-logout">
            <LogOut className="w-4 h-4 mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <TrialBanner sub={sub} className="mb-8" />
        {expiry && (
          <button onClick={() => navigate("/abonnement")} data-testid="hub-expiry-banner"
            className="w-full mb-8 flex items-center gap-3 rounded-3xl bg-amber-50 border border-amber-300 px-5 py-4 text-left hover:bg-amber-100 transition-colors">
            <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
            <span className="text-amber-900 font-semibold">
              {expiry.active
                ? `Votre abonnement expire le ${expiry.until.toLocaleDateString("fr-FR")} (dans ${expiry.days} jour${expiry.days > 1 ? "s" : ""}).`
                : `Votre abonnement a expiré le ${expiry.until.toLocaleDateString("fr-FR")}.`}
              <span className="underline ml-1">Renouveler maintenant</span>
            </span>
          </button>
        )}

        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">Les Gardiens vous accueillent</p>
            <h1 className="font-serif-display italic font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground/90" data-testid="hub-greeting">
              Bonjour {user?.name?.split(" ")[0] || ""},<br />choisissez votre espace.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <GardienBadge id="leontine" eyebrow="Gardienne de l'accueil" testId="hub-gardien-leontine" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {CARDS.map((c) => {
            const locked = homeOnly && c.to === "/tableau-de-bord";
            return <SpaceCard key={c.to} c={c} locked={locked} onClick={() => navigate(locked ? "/abonnement" : c.to)} />;
          })}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <InstallAppButton />
          {LINKS.map(({ to, testId, icon: Icon, label }) => (
            <button key={to} onClick={() => navigate(to)} data-testid={testId}
              className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-foreground/15 bg-card text-foreground/80 font-semibold hover:bg-accent transition-colors">
              <Icon className="w-5 h-5 text-primary" /> {label}
            </button>
          ))}
        </div>
      </main>

      <div className="border-t border-border/40 bg-secondary/15 py-7 px-6 text-center">
        <p className="font-serif-display italic text-xl lg:text-2xl text-foreground/75">Des émotions précieuses pour aujourd'hui et demain.</p>
      </div>
    </div>
  );
}
